import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AppState } from '../../states/app.state';
import { WhiteLabelConfig, loadWhiteLabelConfigSuccess } from '../../states/white-label/white-label.actions';
import { DEFAULT_BRAND_SLOGAN } from '../../states/white-label/brand-slogan.util';

/** Default Goliiive branding used as fallback when no config is available */
const DEFAULTS: WhiteLabelConfig = {
  // Azul de marca (`brand.blue` en tailwind.config.js). Antes era #1897df, un cian
  // que no figuraba en la paleta.
  primary_color: '#1897df',
  secondary_color: '#6366F1',
  tertiary_color: '#141414',
  logo_main_url: '/assets/images/brand/goliiive_logo_blue_no_tagline.svg',
  logo_iso_url: '/assets/images/brand/goliiive_logo_blue_iso.svg',
  logo_white_url: '/assets/images/brand/goliiive_logo_white_no_tagline.svg',
  favicon_url: 'favicon.ico',
  background_auth_url: '/assets/images/general/live_event.jpeg',
  slogan: DEFAULT_BRAND_SLOGAN,
};

@Injectable({ providedIn: 'root' })
export class WhiteLabelService {
  private readonly endpoint = `${environment.apiBase}configuration/white-label/config`;

  constructor(
    private readonly http: HttpClient,
    private readonly store: Store<AppState>
  ) { }

  /**
   * Loads the white-label configuration from the backend.
   * - On success: dispatches to NgRx, saves to localStorage, and applies CSS variables.
   * - On failure: loads from localStorage fallback or uses Goliiive defaults.
   * Called via APP_INITIALIZER so it runs before the app renders.
   */
  async init(): Promise<void> {
    console.log('WhiteLabelService: Initializing...');
    try {
      const config = await firstValueFrom(
        this.http.get<WhiteLabelConfig>(this.endpoint)
      );

      if (config) {
        console.log('WhiteLabelService: Config loaded from backend', config.app_name);
        this.applyConfig(config);
        localStorage.setItem('whiteLabelConfig', JSON.stringify(config));
        this.store.dispatch(loadWhiteLabelConfigSuccess({ config }));
      } else {
        throw new Error('No config returned from backend');
      }
    } catch (error) {
      console.warn('WhiteLabelService: Failed to load from backend, trying localStorage...', error);
      const stored = localStorage.getItem('whiteLabelConfig');
      const config = stored ? (JSON.parse(stored) as WhiteLabelConfig) : DEFAULTS;
      this.applyConfig(config);
      this.store.dispatch(loadWhiteLabelConfigSuccess({ config }));
    }
  }
  /**
   * Applies the configuration to the DOM:
   * - Injects CSS custom properties on :root so all Tailwind color classes update
   * - Swaps the favicon href dynamically
   */
  private applyConfig(config: WhiteLabelConfig): void {
    const root = document.documentElement;
    const primary = config.primary_color ?? DEFAULTS.primary_color;
    const secondary = config.secondary_color ?? DEFAULTS.secondary_color;
    const tertiary = config.tertiary_color ?? DEFAULTS.tertiary_color;

    root.style.setProperty('--color-primary', primary);
    root.style.setProperty('--color-primary-rgb', this.hexToRgb(primary));
    root.style.setProperty('--color-primary-dark', this.darkenColor(primary, 20));
    root.style.setProperty('--color-secondary', secondary);
    root.style.setProperty('--color-secondary-dark', this.darkenColor(secondary, 20));
    root.style.setProperty('--color-tertiary', tertiary);

    // Update favicon: expects <link id="app-favicon" rel="icon"> in index.html
    const favicon = document.getElementById('app-favicon') as HTMLLinkElement | null;
    if (favicon && config.favicon_url) {
      favicon.href = config.favicon_url;
    }
  }

  /**
   * Simple helper to darken a hex color by a percentage.
   */
  private darkenColor(hex: string, percent: number): string {
    // Remove # if present
    hex = hex.replace(/^#/, '');

    // Parse to RGB
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);

    // Darken
    r = Math.floor(r * (1 - percent / 100));
    g = Math.floor(g * (1 - percent / 100));
    b = Math.floor(b * (1 - percent / 100));

    // Ensure within 0-255
    r = Math.max(0, Math.min(255, r));
    g = Math.max(0, Math.min(255, g));
    b = Math.max(0, Math.min(255, b));

    // Back to hex
    const rr = r.toString(16).padStart(2, '0');
    const gg = g.toString(16).padStart(2, '0');
    const bb = b.toString(16).padStart(2, '0');

    return `#${rr}${gg}${bb}`;
  }

  /**
   * Helper to convert hex to RGB string (r, g, b)
   */
  private hexToRgb(hex: string): string {
    hex = hex.replace(/^#/, '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `${r}, ${g}, ${b}`;
  }
}
